<template>
  <div class="ncrm-filter-pills">
    <v-menu
      v-for="filter in filters"
      :key="filter.field"
      location="bottom start"
      :close-on-content-click="filter.type === 'select'"
    >
      <template #activator="{ props }">
        <span class="ncrm-filter-pills__pill" :class="{ 'ncrm-filter-pills__pill--active': filter.active }">
          <button type="button" class="ncrm-filter-pills__pill-main" v-bind="props">
            <span v-if="filter.active" class="ncrm-filter-pills__pill-text">{{ filter.label }}: {{ filter.display }}</span>
            <span v-else class="ncrm-filter-pills__pill-add">+ {{ filter.label }}</span>
          </button>
          <button
            v-if="filter.active"
            type="button"
            class="ncrm-filter-pills__pill-clear"
            :aria-label="`${$t('app.actions.clear')}: ${filter.label}`"
            @click="emitUpdate(filter.field, filter.clearValue)"
          >
            <v-icon size="14">mdi-close</v-icon>
          </button>
        </span>
      </template>

      <v-list v-if="filter.type === 'select'" density="compact" class="ncrm-filter-pills__menu">
        <v-list-item
          v-for="opt in filter.options"
          :key="`${filter.field}-${opt.value}`"
          :active="String(opt.value) === String(filter.value)"
          @click="emitUpdate(filter.field, opt.value)"
        >
          <v-list-item-title>{{ opt.label }}</v-list-item-title>
        </v-list-item>
      </v-list>

      <v-card v-else class="ncrm-filter-pills__menu-card">
        <v-autocomplete
          v-if="filter.type === 'autocomplete'"
          :model-value="filter.value"
          :items="filter.options"
          item-title="label"
          item-value="id"
          :label="filter.label"
          :loading="filter.loading"
          hide-details
          clearable
          autofocus
          @update:model-value="emitUpdate(filter.field, $event ?? filter.clearValue)"
        />
        <v-text-field
          v-else-if="filter.type === 'date'"
          :model-value="filter.value"
          :label="filter.label"
          type="date"
          hide-details
          @update:model-value="emitUpdate(filter.field, $event ?? filter.clearValue)"
        />
        <v-text-field
          v-else
          :model-value="filter.value"
          :label="filter.label"
          hide-details
          autofocus
          @update:model-value="emitUpdate(filter.field, $event ?? filter.clearValue)"
        />
      </v-card>
    </v-menu>

    <slot />

    <span class="ncrm-filter-pills__count">{{ countLabel }}</span>

    <v-tooltip location="top">
      <template #activator="{ props }">
        <v-badge
          class="ncrm-filter-pills__clear-all"
          :model-value="hasActiveFilters"
          :content="activeFilterCount"
          color="primary"
        >
          <v-btn
            v-bind="props"
            icon="mdi-filter-remove-outline"
            :variant="hasActiveFilters ? 'tonal' : 'text'"
            color="primary"
            size="small"
            :aria-label="clearLabel"
            @click="$emit('clear')"
          />
        </v-badge>
      </template>
      <span>{{ clearLabel }}</span>
    </v-tooltip>
  </div>
</template>

<script>
// Bara de filtre „pills” (ui-kit.md → Filtre pills): fiecare filtru este un pill „+ Etichetă” care deschide un meniu
// (select, autocomplete, dată sau text); valoarea activă devine pill plin cu „×”. Datele vin gata calculate din părinte.
export default {
  name: "NcrmFilterPills",
  props: {
    // [{ field, type: "select" | "autocomplete" | "date" | "text", label, options?, value, active, display, clearValue, loading? }]
    filters: { type: Array, required: true },
    countLabel: { type: String, default: "" },
    clearLabel: { type: String, required: true },
    hasActiveFilters: { type: Boolean, default: false },
    activeFilterCount: { type: Number, default: 0 },
  },
  emits: ["update", "clear"],
  methods: {
    emitUpdate(field, value) {
      this.$emit("update", { field, value });
    },
  },
};
</script>

<style scoped>
.ncrm-filter-pills {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.ncrm-filter-pills__pill {
  display: inline-flex;
  align-items: center;
  height: 32px;
  border-radius: var(--app-radius-pill);
  font-size: 13px;
  font-weight: 500;
  background: rgb(var(--v-theme-surface));
  color: rgba(var(--v-theme-on-surface), 0.5);
  border: 1px dashed var(--ncrm-color-border-default);
  transition: border-color 0.12s ease, background-color 0.12s ease, color 0.12s ease;
}

.ncrm-filter-pills__pill:hover {
  color: var(--ncrm-color-text-strong);
  border-color: var(--ncrm-color-border-strong);
}

.ncrm-filter-pills__pill--active {
  background: rgba(var(--v-theme-primary), 0.08);
  color: var(--ncrm-color-text-strong);
  border-style: solid;
  border-color: var(--ncrm-color-primary-border);
}

.ncrm-filter-pills__pill-main,
.ncrm-filter-pills__pill-clear {
  display: inline-flex;
  align-items: center;
  height: 100%;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.ncrm-filter-pills__pill-main {
  padding: 0 12px;
  border-radius: var(--app-radius-pill);
}

.ncrm-filter-pills__pill--active .ncrm-filter-pills__pill-main {
  padding-right: 4px;
}

.ncrm-filter-pills__pill-clear {
  padding: 0 8px 0 4px;
  border-radius: var(--app-radius-pill);
  opacity: 0.55;
}

.ncrm-filter-pills__pill-clear:hover {
  opacity: 1;
}

.ncrm-filter-pills__pill-main:focus-visible,
.ncrm-filter-pills__pill-clear:focus-visible {
  outline: 2px solid var(--ncrm-color-primary-focus);
  outline-offset: 1px;
  opacity: 1;
}

.ncrm-filter-pills__count {
  margin-left: auto;
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), 0.5);
}

.ncrm-filter-pills__clear-all {
  flex-shrink: 0;
}

.ncrm-filter-pills__menu {
  max-height: 320px;
  overflow-y: auto;
}

.ncrm-filter-pills__menu-card {
  padding: 12px;
  min-width: 260px;
}
</style>
